import {useEffect, useState} from "react";
import moment, {Moment} from "moment/moment";
import {useGetBusinessesQuery} from "../../api/rtkQueryApi/BusinessesApi";
import {useGetUsersQuery} from "../../api/rtkQueryApi/UsersApi";
import {
    EuiBasicTableColumn,
    EuiButton,
    EuiFlexGroup,
    EuiFlexItem,
    EuiFormRow,
    EuiInMemoryTable, EuiSpacer,
    EuiText,
} from "@elastic/eui";
import {NumberToCurrency} from "../../utils/Formatters";
import {DateRangePicker} from "../../components/time/DateRangePicker";
import {DownloadAsCsv} from "../../utils/DownloadAsCsv";
import {useGetAllPlansQuery} from "../../api/rtkQueryApi/PlansApi";
import {PlanGetTotalRecurringPrice} from "../../utils/PlanUtils";
import {useGetAllAgreementsQuery} from "../../api/rtkQueryApi/AgreementsApi";
import {ReferenceType} from "../../api/entities/ReferenceType";
import {SignatureState} from "../../api/entities/Agreement";

interface AgreementData {
    createdDate: Moment | null;
    growthCoach: string;
    businessName: string;
    product: string;
    duration: string;
    ncr: string;
    totalContractValue: string;
    revenuePerMonth: string;
}

export const AccountingAgreementsSummary = () => {
    const [startDate, setStartDate] = useState<Moment>(moment().startOf("month"));
    const [endDate, setEndDate] = useState<Moment>(moment().endOf("month"));
    const [minDate] = useState<Moment>();
    const [maxDate] = useState<Moment>();

    const plansQuery = useGetAllPlansQuery();
    const businessesQuery = useGetBusinessesQuery();
    const usersQuery = useGetUsersQuery();
    const agreementsQuery = useGetAllAgreementsQuery();
    const [filteredAgreements, setFilteredAgreements] = useState<AgreementData[]>(
        [],
    );

    useEffect(() => {
        if (plansQuery.data && businessesQuery.data && agreementsQuery.data && usersQuery.data) {
            {
                const agreementsByDate = agreementsQuery.data.filter((agreement) =>
                    moment(agreement.createdAt).isBetween(startDate, endDate),
                );

                const filteredAgreements : AgreementData[] = []
                agreementsByDate.forEach((agreement) => {
                    if(agreement.referenceType !== ReferenceType.Plan || agreement.signatureState !== SignatureState.Signed) return;
                    const plan = plansQuery.data?.find(p => p.id === agreement.referenceId);
                    if(!plan) return;

                    const business = businessesQuery.data?.find(
                        (b) => b.id === plan.businessId,
                    );

                    const user = usersQuery.data?.find(
                        (user) => user.id === business?.salesPersonId,
                    );

                    const growthCouch = `${user?.firstName} ${user?.lastName}`

                    filteredAgreements.push({
                        createdDate: moment(agreement.createdAt),
                        growthCoach: growthCouch,
                        businessName: business?.name ?? "",
                        product: plan.productDetails.name,
                        duration: `${plan.productDetails.contractLengthMonths} months`,
                        ncr: NumberToCurrency(
                            plan.productDetails.netCommissionableRevenue,
                        ),
                        totalContractValue: NumberToCurrency(
                            PlanGetTotalRecurringPrice(plan) * plan.productDetails.contractLengthMonths
                        ),
                        revenuePerMonth: NumberToCurrency(
                            PlanGetTotalRecurringPrice(plan),
                        ),
                    })
                })

                setFilteredAgreements(filteredAgreements);
            }
        }
    }, [plansQuery.data, businessesQuery.data, agreementsQuery.data, usersQuery.data, startDate, endDate]);

    const onDateRangeChange = (startDate: Moment, endDate: Moment) => {
        setStartDate(startDate);
        setEndDate(endDate);
    };

    const getAgreementsColumns: () => EuiBasicTableColumn<AgreementData>[] =
        () => [
            {
                name: "Created Date",
                field: "createdDate",
                render: (createdDate: Moment | null) =>
                    createdDate ? (
                        <EuiText>{moment(createdDate).format("MM/DD/YYYY")}</EuiText>
                    ) : (
                        <EuiText></EuiText>
                    ),
                sortable: true,
            },
            {
                name: "Growth Coach",
                field: "growthCoach",
                sortable: true,
            },
            {
                name: "Business",
                field: "businessName",
                sortable: true,
            },
            {
                name: "Product",
                field: "product",
                sortable: true,
            },
            {
                name: "Duration",
                field: "duration",
            },
            {
                name: "Revenue",
                field: "revenuePerMonth",
                render: (revenue: string) => <EuiText>{revenue} /m</EuiText>,
            },
            {
                name: "NCR",
                field: "ncr",
                sortable: true,
            },
            {
                name: "Total Contract Value",
                field: "totalContractValue",
                sortable: true,
            },
        ];

    const pagination = {
        initialPageSize: 10,
        pageSizeOptions: [10, 25, 50],
    };

    return (
        <>
            <EuiFlexGroup direction={"row"} justifyContent={"spaceBetween"}>
                <EuiFlexItem>
                    <EuiFormRow label={"Date Range"}>
                        <DateRangePicker
                            startDate={startDate}
                            endDate={endDate}
                            onChange={onDateRangeChange}
                            maxDate={maxDate}
                            minDate={minDate}
                        />
                    </EuiFormRow>
                </EuiFlexItem>
                <EuiButton
                    onClick={() => {
                        DownloadAsCsv(filteredAgreements, "agreements.csv");
                    }}
                    disabled={filteredAgreements.length === 0}
                >
                    Download as CSV
                </EuiButton>
            </EuiFlexGroup>
            <EuiSpacer/>
            <EuiFormRow fullWidth label={"Invoices"}>
                <EuiInMemoryTable
                    items={filteredAgreements}
                    itemId="id"
                    columns={getAgreementsColumns()}
                    pagination={pagination}
                    sorting={true}
                />
            </EuiFormRow>
        </>
    );
};
